import {FormEvent, useId, useState} from "react";
import {Alert, Button, Form, Modal} from "react-bootstrap";
import {Link} from "react-router";
import {NewsApi} from "../api/NewsApi";
import {NewsSignupSource} from "../api/models";
import {useAuth} from "../auth/AuthContext";
import {ErrorAlert, fieldError} from "./ErrorAlert";
import {Field, FieldControl} from "./Field";
import {LoadingSpinner} from "./LoadingSpinner";
import {SubmitButton} from "./SubmitButton";
import {announce} from "./announce";
import {emailProblem} from "./format";
import {turnstileAvailable} from "./Turnstile";
import {useTurnstile} from "./useTurnstile";
import {format, news, rich, support} from "../i18n";

const messages = news.dialog;

// "Email me when it's released": a product in Early Access's call to action (its page, and the home page). Opens
// NotifyMeModal. Pages with it are prerendered signed out, which is fine: the dialog only asks who's signed in once open.
export function NotifyMeButton({sku, productName, source, className, size, variant = 'primary'}: {
    sku: string, productName: string, source: NewsSignupSource, className?: string, size?: 'sm' | 'lg', variant?: string
}) {
    const [show, setShow] = useState(false);
    return (
        <>
            <Button className={className} size={size} variant={variant} onClick={() => setShow(true)}>
                <i className="bi bi-envelope me-1" aria-hidden="true"></i>{format(news.button, { product: productName })}
            </Button>
            {show && <NotifyMeModal sku={sku} productName={productName} source={source} onHide={() => setShow(false)}/>}
        </>
    );
}

// The sign-up. Signed out: an email address (initialEmail, when the page's own field had one) and Turnstile's bot
// check, and the API emails a link to confirm it (double opt-in). Signed in: the account's address, one click; added at
// once when it's confirmed, otherwise emailed the link too. The other news is a separate, unticked choice.
export function NotifyMeModal({sku, productName, source, initialEmail = '', onHide}: {
    sku: string, productName: string, source: NewsSignupSource, initialEmail?: string, onHide(): void
}) {
    const titleId = useId();
    const {user, loading: authLoading} = useAuth();
    const [email, setEmail] = useState(initialEmail);
    const [wantsNews, setWantsNews] = useState(false);
    const turnstile = useTurnstile();
    const [validated, setValidated] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<unknown>();
    const [result, setResult] = useState<{ status: 'pending' | 'confirmed', email: string }>();

    const problem = !user ? (!email.trim() ? messages.problems.email : emailProblem(email, { plusAllowed: true })) : undefined;
    const unavailable = !user && !turnstileAvailable();

    const submit = async (e: FormEvent) => {
        e.preventDefault();
        setValidated(true);
        if (problem || unavailable) {
            return;
        }
        setBusy(true);
        setError(undefined);
        let turnstileToken: string | undefined;
        if (!user) {
            turnstileToken = await turnstile.waitForToken();
            if (!turnstileToken) {
                setBusy(false);
                return;
            }
        }
        try {
            const address = user ? user.emailAddress : email.trim();
            const response = await NewsApi.subscribe({
                sku, news: wantsNews, source,
                ...(user ? {} : { email: address, turnstileToken })
            });
            const done = response.status === 'confirmed'
                ? format(messages.signedUp, { email: address, product: productName })
                : format(messages.checkEmail, { email: address });
            announce(done);
            setResult({ status: response.status, email: address });
        } catch (err) {
            setError(err);
            if (!user) {
                turnstile.reset();
            }
        } finally {
            setBusy(false);
        }
    };

    let body;
    if (authLoading) {
        body = <Modal.Body><LoadingSpinner/></Modal.Body>;
    } else if (result) {
        body = (
            <>
                <Modal.Body>
                    <Alert variant="success" role="status" className="mb-0">
                        {result.status === 'confirmed'
                            ? format(messages.signedUp, { email: result.email, product: productName })
                            : format(messages.checkEmail, { email: result.email })}
                    </Alert>
                </Modal.Body>
                <Modal.Footer><Button variant="secondary" onClick={onHide}>{messages.close}</Button></Modal.Footer>
            </>
        );
    } else {
        body = (
            <Form noValidate onSubmit={submit}>
                <Modal.Body>
                    {unavailable && <Alert variant="secondary">{messages.unavailable}</Alert>}
                    <ErrorAlert error={error} shownFields={['email']} messages={{
                        429: messages.tooMany,
                        turnstile_failed: support.newTicketPage.turnstileFailed,
                        503: messages.unavailable
                    }}/>
                    <p>
                        {user
                            ? format(messages.signedInIntro, { email: user.emailAddress, product: productName })
                            : format(messages.intro, { product: productName })}
                    </p>
                    {!user && (
                        <Field className="mb-3" controlId="notify-email" label={messages.email} error={(validated && problem) || fieldError(error, 'email')}>
                            <FieldControl type="email" autoFocus value={email} onChange={e => setEmail(e.target.value)} autoComplete="email" disabled={busy}/>
                        </Field>
                    )}
                    <Form.Check id="notify-news" className="mb-3" label={messages.news} checked={wantsNews}
                                onChange={e => setWantsNews(e.target.checked)} disabled={busy}/>
                    <p className="small text-body-secondary mb-0">
                        {rich(messages.privacy, { link: text => <Link to="/privacy" onClick={onHide}>{text}</Link> })}
                    </p>
                    {!user && !unavailable && (
                        <div className="mt-3">
                            {turnstile.widget}
                            {turnstile.failed && <Alert variant="warning">{support.newTicketPage.turnstileUnavailable}</Alert>}
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={onHide} disabled={busy}>{messages.cancel}</Button>
                    <SubmitButton busy={busy}>{messages.submit}</SubmitButton>
                </Modal.Footer>
            </Form>
        );
    }

    return (
        <Modal aria-labelledby={titleId} show onHide={onHide} centered>
            <Modal.Header closeButton>
                <Modal.Title id={titleId}>{format(messages.title, { product: productName })}</Modal.Title>
            </Modal.Header>
            {body}
        </Modal>
    );
}
